import React, { useState } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { IconButton, Menu, Divider } from "react-native-paper";
import { COLORS, STRINGS } from "../../constants";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
/**
 * DetailButtonArray Component
 *
 * This component renders an array of button icons that trigger actions when pressed.
 * Each button represents a particular action or feature, and it can be customized
 * with different icons and sizes. The icons are passed using the FontAwesomeIcon
 * component and are displayed using the IconButton component from react-native-paper.
 *
 * The component ensures the icons are evenly spaced in a row and provides a clean,
 * consistent look with customizable sizes and colors. It is ideal for use in
 * interfaces where multiple actions need to be presented to the user at once.
 *
 * Props:
 * - FontAwesomeIcon (component): The icon component used to render FontAwesome icons.
 * - buttons (array): An array of button objects, each containing an `icon` property
 *   to specify the FontAwesome icon to be used.
 * - onPress (function): The function to be called when any button is pressed,
 *   passing the corresponding button object.
 * - iconSize (number): The size of the icon to be rendered within each button.
 *
 * Usage:
 * This component is used in user interfaces where multiple button actions are needed
 * in a compact layout, especially in detail pages where various options like edit,
 * delete, or share are required.
 *
 **/

const DetailButtonArray = ({ FontAwesomeIcon, buttons, onPress, iconSize }) => {
  const [menuVisible, setMenuVisible] = useState(false);

  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  // Separate the first two buttons and the rest for the dropdown menu
  const firstTwoButtons = buttons.slice(0, 2);
  const remainingButtons = buttons
    .slice(2)
    .filter((button) => !((Platform.isPad || Platform.OS === "web") && button.label === "Call"));

  return (
    <View
      style={[
        styles.container,
        Platform.OS === "android" && remainingButtons.length > 0 ? { alignSelf: "flex-end" } : { alignSelf: "center" },
      ]}
    >
      {/* Render the first two buttons */}
      {firstTwoButtons.map((button, index) => (
        <IconButton
          key={index}
          icon={() => <FontAwesomeIcon icon={button.icon} size={iconSize} color={COLORS.border} />}
          testID={button.testID}
          onPress={() => onPress(button)}
          size={24}
          accessibilityLabel={button.label}
          style={styles.button}
        />
      ))}

      {/* Render the dropdown menu if there are remaining buttons */}
      {remainingButtons.length > 0 && (
        <Menu
          visible={menuVisible}
          onDismiss={closeMenu}
          anchor={
            <IconButton
              icon={() => <FontAwesomeIcon icon={faEllipsisV} size={iconSize} color={COLORS.border} />}
              onPress={openMenu}
              accessibilityLabel={STRINGS.moreOptions}
            />
          }
          style={{
            top: Platform.OS != "ios" ? 50 : 100,
          }}
        >
          {remainingButtons.map((button, index) => (
            <React.Fragment key={index}>
              <Menu.Item
                leadingIcon={() => <FontAwesomeIcon icon={button.icon} size={iconSize} color={COLORS.border} />}
                title={button.label}
                onPress={() => {
                  closeMenu();
                  onPress(button);
                }}
              />
              {index < remainingButtons.length - 1 && <Divider style={styles.divider} />}
            </React.Fragment>
          ))}
        </Menu>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
  },
  button: {
    marginHorizontal: 10,
  },
  divider: {
    borderBottomColor: COLORS.border,
    borderBottomWidth: 0.5,
    width: "75%",
    alignSelf: "center",
  },
});

export default DetailButtonArray;
